/**
 * Cron — the six-field EventBridge Scheduler expression, validated and read back in English.
 *
 * Extracted from ScheduleEditor so it can be unit-tested without mounting a React tree.
 * `services/api/validate.py` is the authority; this module's only job is to say the same
 * thing before the round trip, so that the form never accepts an expression the API will
 * refuse, and never describes one differently from how EventBridge will fire it.
 *
 * The two ways this has already been wrong, both of which the tests now pin:
 *   - five fields instead of six. Every schedule created through the form was refused on
 *     save, and the hint under the box confidently named the five it wanted.
 *   - day-of-week read as ISO (1=Monday). EventBridge is 1=Sunday, so "30 7 ? * 1-5 *"
 *     fires Sun–Thu while the form called it "Every Mon–Fri" — a wrong reading on the one
 *     form that decides what a client receives.
 */

import { ordinal } from '@/lib/format';


const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
const DOW_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

interface CronFieldSpec {
  label: string;
  min: number;
  max: number;
  /** Three-letter aliases, index 0 = `offset`. */
  names?: readonly string[];
  offset?: number;
  /** `?` ("no specific value") is legal only in day-of-month and day-of-week. */
  wildcardQuestion?: boolean;
}

const CRON_FIELDS: readonly CronFieldSpec[] = [
  { label: 'minute', min: 0, max: 59 },
  { label: 'hour', min: 0, max: 23 },
  { label: 'day of month', min: 1, max: 31, wildcardQuestion: true },
  { label: 'month', min: 1, max: 12, names: MONTH_NAMES, offset: 1 },
  { label: 'day of week', min: 1, max: 7, names: DOW_NAMES, offset: 1, wildcardQuestion: true },
  { label: 'year', min: 1970, max: 2199 },
];

const DOM_INDEX = 2;
const DOW_INDEX = 4;

function resolveCronValue(token: string, spec: CronFieldSpec): number | null {
  if (/^\d+$/.test(token)) {
    const value = Number(token);
    return value >= spec.min && value <= spec.max ? value : null;
  }
  if (spec.names) {
    const index = spec.names.indexOf(token.toUpperCase());
    if (index !== -1) return index + (spec.offset ?? 0);
  }
  return null;
}

function cronFieldError(field: string, spec: CronFieldSpec): string | null {
  if (field === '') return `The ${spec.label} field is empty.`;
  for (const term of field.split(',')) {
    if (term === '') return `The ${spec.label} field has an empty item in its list.`;

    const [base, step] = term.split('/') as [string, string | undefined];
    if (term.split('/').length > 2) return `"${term}" has more than one step in the ${spec.label} field.`;

    if (step !== undefined) {
      if (!/^\d+$/.test(step) || Number(step) < 1) {
        return `"${term}" needs a whole-number step, e.g. */15, in the ${spec.label} field.`;
      }
    }

    if (base === '*') continue;
    if (base === '?') {
      if (spec.wildcardQuestion) continue;
      return `"?" is only allowed in day-of-month and day-of-week, not the ${spec.label} field.`;
    }

    if (base.includes('-')) {
      const [start, end] = base.split('-') as [string, string | undefined];
      if (end === undefined || base.split('-').length > 2) {
        return `"${term}" is not a valid range in the ${spec.label} field.`;
      }
      const startValue = resolveCronValue(start, spec);
      const endValue = resolveCronValue(end, spec);
      if (startValue === null || endValue === null) {
        return `"${term}" is outside ${spec.min}–${spec.max} for the ${spec.label} field.`;
      }
      if (startValue > endValue) return `"${term}" runs backwards in the ${spec.label} field.`;
      continue;
    }

    if (resolveCronValue(base, spec) === null) {
      return `"${term}" is not a valid ${spec.label} (${spec.min}–${spec.max}).`;
    }
  }
  return null;
}

/** The single problem with a cron expression, or null. Six fields, IST, EventBridge syntax. */
export function cronError(expression: string): string | null {
  const trimmed = expression.trim();
  if (!trimmed) return 'A schedule needs a cron expression.';
  const fields = trimmed.split(/\s+/);
  if (fields.length !== 6) {
    return (
      `Use six fields — minute hour day-of-month month day-of-week year — not ${fields.length}. ` +
      `'30 7 * * ? *' is daily at 07:30 IST.`
    );
  }
  // L / W / # are legal EventBridge syntax the API refuses, because neither side can
  // compute a next fire time for them. Checked before the per-field ranges, which would
  // otherwise report "L" as an out-of-range day of month and bury the real reason.
  if (/[LW#]/i.test(trimmed)) {
    return 'L, W and # are not scheduled by this console. Use an explicit day number or day name.';
  }

  for (let index = 0; index < CRON_FIELDS.length; index += 1) {
    const spec = CRON_FIELDS[index]!;
    const error = cronFieldError(fields[index]!, spec);
    if (error) return error;
  }

  // EventBridge cannot honour a restriction on both day fields, and refuses a '?' in
  // both. The same two sentences the API answers with, said before the round trip.
  const dom = fields[DOM_INDEX]!.trim();
  const dow = fields[DOW_INDEX]!.trim();
  if (dom !== '?' && dow !== '?') {
    return (
      "Use '?' in exactly one of day-of-month and day-of-week. " +
      "'30 7 * * ? *' is daily; '30 7 ? * MON-FRI *' is weekdays."
    );
  }
  if (dom === '?' && dow === '?') {
    return "'?' in both day-of-month and day-of-week matches nothing. Use '*' in one of them.";
  }

  return null;
}

const pad2 = (value: number): string => (value < 10 ? `0${value}` : String(value));

function describeDow(field: string): string | null {
  if (field === '*' || field === '?') return null;
  const label = (token: string): string | null => {
    const value = resolveCronValue(token, CRON_FIELDS[DOW_INDEX]!);
    // EventBridge day-of-week is 1-based: 1=Sunday.
    return value === null ? null : (DOW_LABELS[value - 1] ?? null);
  };
  if (field.includes('/')) return null;
  if (field.includes('-') && !field.includes(',')) {
    const [start, end] = field.split('-') as [string, string | undefined];
    const a = label(start);
    const b = end === undefined ? null : label(end);
    return a && b ? `${a}–${b}` : null;
  }
  const parts = field.split(',').map(label);
  if (parts.some((part) => part === null)) return null;
  return joinDays(parts as string[], field.split(','));
}

/**
 * "Mon–Fri", not "Mon, Tue, Wed, Thu, Fri". The pickers emit a comma list — there is no
 * range to preserve — so the collapsing happens here, on the way to the reader. Weekdays
 * are the common schedule and the long form buries the time at the end of the line.
 */
function joinDays(labels: string[], tokens: string[]): string {
  const spec = CRON_FIELDS[DOW_INDEX]!;
  const values = tokens.map((token) => resolveCronValue(token, spec));
  if (values.some((value) => value === null)) return labels.join(', ');

  const runs: string[] = [];
  let start = 0;
  for (let index = 1; index <= values.length; index += 1) {
    const contiguous = index < values.length && values[index]! === values[index - 1]! + 1;
    if (contiguous) continue;
    const end = index - 1;
    // A run of two reads better as "Sat, Sun" than as "Sat–Sun".
    runs.push(end - start >= 2 ? `${labels[start]}–${labels[end]}` : labels.slice(start, end + 1).join(', '));
    start = index;
  }
  return runs.join(', ');
}

/**
 * Plain-English reading of a cron expression, or null when the expression is more
 * exotic than this console can honestly summarise. Returning null is deliberate —
 * a wrong description is worse than none on a form that decides what a client gets.
 */
export function describeCron(expression: string): string | null {
  if (cronError(expression)) return null;
  const [minute, hour, dom, month, dow, year] = expression.trim().split(/\s+/) as [
    string,
    string,
    string,
    string,
    string,
    string,
  ];

  // A year restriction makes every reading below conditional ("every day at 07:30, but
  // only in 2027"). Rather than bolt a clause on, say nothing — the rule for this
  // function is that a wrong description is worse than none.
  if (year !== '*') return null;

  const everyDom = dom === '*' || dom === '?';
  const everyMonth = month === '*' || month === '?';
  const everyDow = dow === '*' || dow === '?';

  const minuteStep = /^\*\/(\d+)$/.exec(minute);
  if (minuteStep && hour === '*' && everyDom && everyMonth && everyDow) {
    return `Every ${minuteStep[1]} minutes, IST`;
  }

  if (!/^\d+$/.test(minute)) return null;
  const minuteValue = Number(minute);

  if (hour === '*' && everyDom && everyMonth && everyDow) {
    return `Every hour at :${pad2(minuteValue)}, IST`;
  }

  const hourStep = /^\*\/(\d+)$/.exec(hour);
  if (hourStep && everyDom && everyMonth && everyDow) {
    return `Every ${hourStep[1]} hours at :${pad2(minuteValue)}, IST`;
  }

  if (!/^\d+$/.test(hour)) return null;
  const at = `${pad2(Number(hour))}:${pad2(minuteValue)} IST`;

  if (everyMonth && everyDom && everyDow) return `Every day at ${at}`;

  if (everyMonth && everyDom) {
    const days = describeDow(dow);
    return days ? `Every ${days} at ${at}` : null;
  }

  if (everyMonth && everyDow && /^\d+$/.test(dom)) {
    return `On the ${ordinal(Number(dom))} of every month at ${at}`;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Building and reading back a schedule the form can express
// ---------------------------------------------------------------------------

/**
 * The shapes the pickers can express. `custom` is the escape hatch: an expression that
 * this module can validate and usually describe, but cannot round-trip into controls.
 * It exists so an operator is never *locked out* of a schedule the API would accept —
 * not as the normal way to set a time.
 */
export type Frequency = 'daily' | 'weekly' | 'monthly' | 'custom';

export interface CronParts {
  frequency: Exclude<Frequency, 'custom'>;
  /** 0–23, IST. */
  hour: number;
  /** 0–59. */
  minute: number;
  /** EventBridge day-of-week, 1=Sunday. Weekly only. */
  weekdays: number[];
  /** 1–31. Monthly only. */
  dayOfMonth: number;
}

export const WEEKDAY_LABELS = DOW_LABELS;

/** 1=Sunday .. 7=Saturday, for a picker that shows Mon first or Sun first as it likes. */
export const WEEKDAY_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;

const pad = (value: number): string => (value < 10 ? `0${value}` : String(value));

/**
 * The six-field expression for a set of picker values. Always emits `?` in whichever day
 * field is unconstrained, because EventBridge refuses an expression that constrains both.
 */
export function buildCron(parts: CronParts): string {
  const { frequency, hour, minute } = parts;
  const at = `${minute} ${hour}`;
  if (frequency === 'weekly') {
    const days = [...new Set(parts.weekdays)].sort((a, b) => a - b);
    return `${at} ? * ${days.join(',')} *`;
  }
  if (frequency === 'monthly') {
    return `${at} ${parts.dayOfMonth} * ? *`;
  }
  return `${at} * * ? *`;
}

/** Every value a day-of-week field names, or null if it is more exotic than the picker. */
function expandWeekdays(field: string): number[] | null {
  const spec = CRON_FIELDS[DOW_INDEX]!;
  const out: number[] = [];
  for (const term of field.split(',')) {
    if (term.includes('/')) return null;
    if (term.includes('-')) {
      const [start, end] = term.split('-') as [string, string | undefined];
      if (end === undefined) return null;
      const from = resolveCronValue(start, spec);
      const to = resolveCronValue(end, spec);
      if (from === null || to === null || from > to) return null;
      for (let value = from; value <= to; value += 1) out.push(value);
      continue;
    }
    const value = resolveCronValue(term, spec);
    if (value === null) return null;
    out.push(value);
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * Picker values for an expression, or null when the form cannot represent it.
 *
 * Returning null is how an existing schedule written by hand — an every-15-minutes step,
 * a month restriction — keeps working: the editor drops to the custom field with the
 * expression intact rather than quietly rewriting it into the nearest thing it can draw.
 */
export function parseCron(expression: string): CronParts | null {
  if (cronError(expression)) return null;
  const [minute, hour, dom, month, dow, year] = expression.trim().split(/\s+/) as [
    string, string, string, string, string, string,
  ];
  if (year !== '*' || month !== '*') return null;
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) return null;
  const at = { hour: Number(hour), minute: Number(minute) };

  const everyDom = dom === '*' || dom === '?';
  const everyDow = dow === '*' || dow === '?';

  if (everyDom && everyDow) {
    return { frequency: 'daily', ...at, weekdays: [], dayOfMonth: 1 };
  }
  if (everyDom) {
    const weekdays = expandWeekdays(dow);
    return weekdays && weekdays.length ? { frequency: 'weekly', ...at, weekdays, dayOfMonth: 1 } : null;
  }
  if (everyDow && /^\d+$/.test(dom)) {
    return { frequency: 'monthly', ...at, weekdays: [], dayOfMonth: Number(dom) };
  }
  return null;
}

/** "07:30" for a pair of picker values — the one place the display format is decided. */
export function formatTime(hour: number, minute: number): string {
  return `${pad(hour)}:${pad(minute)}`;
}
